/**
 * SignupWizard Component
 * Simplified signup wizard — email verification → password → free trial starts.
 *
 * Flow:
 * 1. Email: enter email address
 * 2. Verify: 6-digit code from email (custom SES verification)
 * 3. Password: create password → Cognito signUp (PreSignUp auto-confirms) + signIn
 * 4. Welcome: 30-day free trial started, auto-redirect to /add
 *
 * Plan selection and payment are handled separately via the /upgrade page.
 */

import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { PageLayout } from '@/components';
import { useAuthContext } from '@/contexts/AuthContext';
import { Card, IconContainer } from '@/components/ui';
import { useSignupWizard, type WizardStep } from './hooks/useSignupWizard';
import { StepIndicator } from './components/StepIndicator';
import { EmailStep } from './steps/EmailStep';
import { VerifyEmailStep } from './steps/VerifyEmailStep';
import { PasswordStep } from './steps/PasswordStep';
import { WelcomeStep } from './steps/WelcomeStep';
import { authApi } from '@/services/authApi';
import { errorMessages } from '@/lib/errors';

export const SignupWizard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { register, login, isAuthenticated } = useAuthContext();
  const wizard = useSignupWizard();

  // Redirect if already authenticated (unless completing signup in steps 3-4)
  useEffect(() => {
    if (isAuthenticated && wizard.state.currentStep < 3) {
      navigate('/add', { replace: true });
    }
  }, [isAuthenticated, navigate, wizard.state.currentStep]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [wizard.state.currentStep]);

  // Step 1: Send verification code
  const handleEmailSubmit = useCallback(async () => {
    wizard.setError(null);
    wizard.setIsLoading(true);

    try {
      await authApi.sendVerificationCode(wizard.state.email);
      wizard.nextStep();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('signup.failedSendCode');
      wizard.setError(message);
    } finally {
      wizard.setIsLoading(false);
    }
  }, [wizard]);

  // Step 2: Verify the code
  const handleVerifySubmit = useCallback(async () => {
    wizard.setError(null);
    wizard.setIsLoading(true);

    try {
      await authApi.verifyCode(wizard.state.email, wizard.state.verificationCode);
      wizard.setIsEmailVerified(true);
      wizard.nextStep();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('signup.failedVerifyCode');
      wizard.setError(message);
    } finally {
      wizard.setIsLoading(false);
    }
  }, [wizard]);

  // Step 2: Resend code
  const handleResendCode = useCallback(async () => {
    wizard.setError(null);
    wizard.setVerificationCode('');
    try {
      await authApi.sendVerificationCode(wizard.state.email);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('signup.failedResendCode');
      wizard.setError(message);
    }
  }, [wizard]);

  // Step 3: Create account (Cognito signUp + signIn) → free trial starts
  const handlePasswordSubmit = useCallback(async () => {
    wizard.setError(null);
    wizard.setIsLoading(true);

    try {
      const signupDate = new Date().toISOString();

      // Register with Cognito — PreSignUp trigger auto-confirms since email is verified
      await register(wizard.state.email, wizard.state.password, {
        plan: 'free',
        signupDate,
      });

      // Sign in immediately (user is auto-confirmed)
      await login(wizard.state.email, wizard.state.password);

      // Go to Welcome step
      wizard.nextStep();

      // Auto-redirect to app after welcome
      setTimeout(() => {
        navigate('/add');
      }, 3000);
    } catch (err) {
      wizard.setError(errorMessages.registration(err));
    } finally {
      wizard.setIsLoading(false);
    }
  }, [wizard, register, login, navigate]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    wizard.prevStep();
  }, [wizard]);

  // Render current step
  const renderStep = () => {
    switch (wizard.state.currentStep) {
      case 1:
        return (
          <EmailStep
            email={wizard.state.email}
            error={wizard.state.error}
            isLoading={wizard.state.isLoading}
            onEmailChange={wizard.setEmail}
            onSubmit={handleEmailSubmit}
            canProceed={wizard.canProceed()}
          />
        );

      case 2:
        return (
          <VerifyEmailStep
            email={wizard.state.email}
            verificationCode={wizard.state.verificationCode}
            error={wizard.state.error}
            isLoading={wizard.state.isLoading}
            onVerificationCodeChange={wizard.setVerificationCode}
            onBack={handleBack}
            onSubmit={handleVerifySubmit}
            onResendCode={handleResendCode}
            canProceed={wizard.canProceed()}
          />
        );

      case 3:
        return (
          <PasswordStep
            password={wizard.state.password}
            confirmPassword={wizard.state.confirmPassword}
            error={wizard.state.error}
            isLoading={wizard.state.isLoading}
            onPasswordChange={wizard.setPassword}
            onConfirmPasswordChange={wizard.setConfirmPassword}
            onBack={handleBack}
            onSubmit={handlePasswordSubmit}
            canProceed={wizard.canProceed()}
          />
        );

      case 4:
        return <WelcomeStep />;

      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-12 sm:pt-24">
        <div className="max-w-lg w-full">
          {/* Back button (only on step 1) */}
          {wizard.state.currentStep === 1 && (
            <button
              onClick={() => navigate(-1)}
              className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </button>
          )}

          {/* Header (only on step 1) */}
          {wizard.state.currentStep === 1 && (
            <div className="text-center mb-8">
              <IconContainer variant="success" size="xl" className="mx-auto mb-4">
                <UserPlus className="w-8 h-8" />
              </IconContainer>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                {t('signup.createAccount')}
              </h1>
              <p className="text-slate-600">
                {t('signup.startFreeTrial')}
              </p>
            </div>
          )}

          {/* Step Indicator (hidden on welcome step) */}
          {wizard.state.currentStep !== 4 && (
            <StepIndicator
              currentStep={wizard.state.currentStep}
              steps={[
                { number: 1 as WizardStep, label: t('signup.stepEmail') },
                { number: 2 as WizardStep, label: t('signup.stepVerify') },
                { number: 3 as WizardStep, label: t('signup.stepPassword') },
              ]}
            />
          )}

          {/* Step Content */}
          <Card variant="form" padding="xl">
            {renderStep()}
          </Card>

          {/* Sign in link (only on step 1) */}
          {wizard.state.currentStep === 1 && (
            <p className="mt-6 text-center text-slate-600">
              {t('signup.alreadyHaveAccount')}{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
              >
                {t('signup.signIn')}
              </button>
            </p>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default SignupWizard;
